/* src/views/logements/LogementsV2.jsx — LA DESTINATION ⌂ Logements (lot 4,
   2026-08-28) : la liste « Le parc » triée par avancement, puis la fiche à
   dix sous-onglets du prototype. Remplace le montage de LogementsPanel à
   l'onglet `logements` — LA MÉCANIQUE NE BOUGE PAS : le brouillon cfg et
   ses handlers vivent dans useParcDraft (extraits fidèlement du panel),
   les écritures immédiates DB V2 restent portées par chaque sous-onglet.

   Contrat de non-régression : docs/handoff/INVENTAIRE_LOT4_LOGEMENTS.md. */

import { useState, useMemo, useEffect } from 'react'
import { supabase } from '../../supabase'
import { getActiveMappedLogementIds } from '../../lib/beds24-mapping-lookup'
import { propDisplayName } from '../../lib/format'
import { estProprioVide } from '../../lib/proprio-creation'
import useParcDraft, { hasChambres } from './useParcDraft'
import { BandeauParc, CarteLogement, ActionsParc } from './proto/ParcProto'
import { BandeauFiche, BasculeFiche, FICHE_ONGLETS } from './proto/FicheChrome'
import FicheInfosCtrl from './FicheInfosCtrl'
import FicheInstructionsCtrl from './FicheInstructionsCtrl'
import FicheLivretCtrl from './FicheLivretCtrl'
import FicheChecklistCtrl from './FicheChecklistCtrl'
import FicheLingeCtrl from './FicheLingeCtrl'
import FicheDocsCtrl from './FicheDocsCtrl'
import FicheSiteCtrl from './FicheSiteCtrl'
import FicheServicesCtrl from './FicheServicesCtrl'
import FicheAvisCtrl from './FicheAvisCtrl'
import FicheSavoirCtrl from './FicheSavoirCtrl'
import Feuille from '../accueil/Feuille'
import ADemarcherCtrl from './ADemarcherCtrl'
import { FeuilleNouveauProprio, ProprietairesSansLogement } from './NouveauProprio'
import { prospectsApi } from '../../lib/prospects-api'
import { poserParcDirty } from './parcDirty'
import '../../styles/fbz-admin.css'

/* Nombres en toutes lettres pour la note de pied (registre du proto). */
const LETTRES = ['zéro', 'une', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept',
  'huit', 'neuf', 'dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize',
  'dix-sept', 'dix-huit', 'dix-neuf', 'vingt']
function enLettres(n, majuscule = false) {
  const s = LETTRES[n] || String(n)
  return majuscule ? s.charAt(0).toUpperCase() + s.slice(1) : s
}

/* ── les jalons réels de la liste (Beds24 · Direct · Checklist) ─────
   Trois lectures légères, fail-soft : un jalon illisible s'affiche gris
   MAIS la liste le dit (bandeau discret) — jamais un « à faire » menteur
   silencieux (leçon « une absence n'est pas une cause »). */
function useJalonsParc(actif) {
  const [beds24Ids, setBeds24Ids] = useState(null)   // Set | null = pas chargé
  const [directIds, setDirectIds] = useState(null)
  const [covers, setCovers] = useState(null)          // Map logId → url
  const [erreurs, setErreurs] = useState([])

  useEffect(() => {
    if (!actif) return undefined
    let vivant = true
    ;(async () => {
      try {
        const ids = await getActiveMappedLogementIds()
        if (vivant) setBeds24Ids(ids)
      } catch (e) {
        console.error('[parc] jalon Beds24', e?.message || e)
        if (vivant) setErreurs((prev) => [...prev, 'Beds24'])
      }
    })()
    ;(async () => {
      try {
        const { data, error } = await supabase.from('logement_site_content')
          .select('logement_id, statut_publication')
          .eq('statut_publication', 'published')
        if (error) throw error
        if (vivant) setDirectIds(new Set((data || []).map((r) => r.logement_id)))
      } catch (e) {
        console.error('[parc] jalon Direct', e?.message || e)
        if (vivant) setErreurs((prev) => [...prev, 'Direct'])
      }
    })()
    ;(async () => {
      try {
        const { data, error } = await supabase.from('logement_gallery')
          .select('logement_id, public_url')
          .eq('is_cover', true)
        if (error) throw error
        if (vivant) setCovers(new Map((data || []).map((r) => [r.logement_id, r.public_url])))
      } catch (e) {
        console.error('[parc] photos cover', e?.message || e)
        if (vivant) setErreurs((prev) => [...prev, 'photos'])
      }
    })()
    return () => { vivant = false }
  }, [actif])

  return { beds24Ids, directIds, covers, erreurs }
}

/* Checklist « faite » = au moins une pièce avec au moins un item (cfg). */
function checklistFaite(entity) {
  const pieces = entity?.checklists?.menage?.pieces
  return Array.isArray(pieces) && pieces.some((p) => Array.isArray(p.items) && p.items.length > 0)
}

export default function LogementsV2(props) {
  const { cfg, save, data, saveData, factLog, saveLog, invoicesList, invoicesRaw, incidents } = props
  const draft = useParcDraft({ cfg, save, data, saveData, factLog, saveLog })
  const { c, dirty, savedAt, flat, selected, selectedId, setSelectedId } = draft
  const [onglet, setOnglet] = useState('info')
  const [feuilleNouveau, setFeuilleNouveau] = useState(false)
  // « ＋ Propriétaire » ouvre un FORMULAIRE (incident 30/09) ; la fiche créée
  // est ensuite montrée dans « Propriétaires sans logement ».
  const [feuilleProprio, setFeuilleProprio] = useState(false)
  const [nouveauPropId, setNouveauPropId] = useState(null)
  // LOT 4 OPÉRATEUR B — la bascule de tête « Mon parc / À démarcher ».
  // Le compteur « À démarcher » = annonces à regarder (pas_contacte non
  // masquées) : d'abord le comptage serveur léger (?action=count, zéro PII),
  // puis le nombre RÉEL de la liste chargée quand la facette est ouverte.
  const [vueLog, setVueLog] = useState('parc')
  const [aRegarder, setARegarder] = useState(null)
  useEffect(() => {
    let vivant = true
    prospectsApi.fetchProspectsCount()
      .then((r) => { if (vivant && typeof r?.aRegarder === 'number') setARegarder(r.aRegarder) })
      .catch((e) => { console.error('[parc] compteur à démarcher', e?.message || e) })
    return () => { vivant = false }
  }, [])

  // Garde anti-perte (décision 2026-08-28) : le registre suit dirty, la page
  // prévient à la fermeture du navigateur, App prévient au départ de la
  // destination (il consulte parcEstDirty avant de basculer).
  useEffect(() => {
    poserParcDirty(dirty)
    return () => poserParcDirty(false)
  }, [dirty])
  useEffect(() => {
    const garde = (e) => {
      if (!dirty) return undefined
      e.preventDefault()
      e.returnValue = ''
      return ''
    }
    window.addEventListener('beforeunload', garde)
    return () => window.removeEventListener('beforeunload', garde)
  }, [dirty])

  // Feuilles louables = chambres + logements plats (jamais le parent sous-loc).
  const feuilles = useMemo(
    () => flat.filter((f) => f.isChambre || !hasChambres(f.entity)),
    [flat])

  const jalons = useJalonsParc(!selectedId)

  // Arrivée depuis Paramètres → Espace voyageur (sessionStorage, consommé
  // une fois — même contrat que LogementsPanel).
  useEffect(() => {
    let target = null
    try {
      target = sessionStorage.getItem('fbz8-goto-logement')
      if (target) sessionStorage.removeItem('fbz8-goto-logement')
    } catch { /* sessionStorage indisponible → ignorer */ }
    if (!target) return
    if (feuilles.some((f) => f.entity.id === target)) {
      setSelectedId(target)
      setOnglet('info')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── LA LISTE « Le parc » / la facette « À démarcher » ─────────────
  if (!selected) {
    const basculeEl = (
      <div className="bascule" style={{ margin: '0 0 14px' }} data-testid="logements-bascule">
        {[['parc', 'Mon parc', feuilles.length], ['demarcher', 'À démarcher', aRegarder]].map(([id, lb, n]) => (
          <button key={id} className={vueLog === id ? 'on' : ''}
            data-testid={`logements-bascule-${id}`} onClick={() => setVueLog(id)}>{lb}
            {n !== null && n !== undefined && (
              <span style={{ fontSize: '9.5px', opacity: 0.6, marginLeft: '5px' }}>{n}</span>
            )}</button>
        ))}
      </div>
    )
    if (vueLog === 'demarcher') {
      return <ADemarcherCtrl bascule={basculeEl} onARegarder={setARegarder} />
    }
    const lignes = feuilles.map((f) => {
      const id = f.entity.id
      return {
        f,
        id,
        nom: f.entity.nom_complet || f.entity.nom || id,
        proprio: propDisplayName(f.prop) || f.prop.nom || '',
        photoUrl: jalons.covers ? (jalons.covers.get(id) || null) : null,
        j: {
          beds24: jalons.beds24Ids ? jalons.beds24Ids.has(id) : false,
          direct: jalons.directIds ? jalons.directIds.has(id) : false,
          checklist: checklistFaite(f.entity),
        },
      }
    })
    // Tri par avancement, stable dans chaque groupe (ordre cfg).
    const avance = (l) => (l.j.beds24 ? 1 : 0) + (l.j.direct ? 1 : 0) + (l.j.checklist ? 1 : 0)
    const triees = [...lignes].sort((a, b) => avance(b) - avance(a))

    const nbBiens = (c.props || []).reduce((n, p) => n + (p.logs || []).length, 0)
    const parent = (c.props || []).flatMap((p) => p.logs || []).find(hasChambres)
    const nbChambres = parent ? (parent.chambres || []).length : 0
    const sousTitre = parent
      ? `${enLettres(nbBiens, true)} biens, dont une maison de ${enLettres(nbChambres)} chambres`
      : `${enLettres(nbBiens, true)} biens`
    const note = parent
      ? `${enLettres(feuilles.length, true)} feuilles louables. Les ${enLettres(nbBiens)} biens ne servent qu'à la compta, où le loyer se paie par maison et non par chambre.`
      : `${enLettres(feuilles.length, true)} feuilles louables.`

    return (
      <>
        <BandeauParc sousTitre={sousTitre} jauges={[
          { id: 'beds24', lb: 'Branchés à Beds24', n: lignes.filter((l) => l.j.beds24).length, total: lignes.length },
          { id: 'direct', lb: 'Ouverts au direct', n: lignes.filter((l) => l.j.direct).length, total: lignes.length },
          { id: 'checklist', lb: 'Checklist faite', n: lignes.filter((l) => l.j.checklist).length, total: lignes.length },
        ]} />
        <div className="page">
          {basculeEl}
          <div className="info">Classés par avancement : ce qui est prêt en haut, ce qui reste à faire en bas.</div>
          {jalons.erreurs.length > 0 && (
            <div className="info" data-testid="parc-jalons-erreur"
              style={{ borderColor: '#e8c9a0', background: '#fdf6ec', color: '#8a5a17' }}>
              Jalons {jalons.erreurs.join(' · ')} illisibles pour l'instant — les
              pastilles grises de ces colonnes ne font pas foi. Recharger la page.
            </div>
          )}
          {triees.map((l) => (
            <CarteLogement key={l.id} id={l.id} nom={l.nom} proprio={l.proprio}
              photoUrl={l.photoUrl} jalons={l.j}
              onOuvrir={() => {
                if (dirty && !window.confirm('Des saisies attendent la pastille « Enregistrer » (elles couvrent tout le parc, rien n\'est perdu en changeant de logement — mais rien n\'est encore sauvé). Continuer sans enregistrer ?')) return
                setSelectedId(l.id); setOnglet('info')
              }} />
          ))}
          <ProprietairesSansLogement props={c.props} nouveauId={nouveauPropId}
            invoicesList={invoicesList} incidents={incidents}
            onAjouterLogement={(propId) => {
              const propIdx = (c.props || []).findIndex((p) => p.id === propId)
              if (propIdx < 0) return
              draft.addLog(propIdx)
              setOnglet('info')
            }}
            onRetirer={async (propId) => {
              const r = await draft.retirerProprio(propId, { invoicesList, incidents })
              if (r.ok && r.avertissement) alert(r.avertissement)
              return r
            }} />
          <ActionsParc
            onAddProprio={() => setFeuilleProprio(true)}
            onAddLogement={() => setFeuilleNouveau(true)}
            onAddSousLoc={() => { draft.addSousLoc(); setOnglet('info') }}
            sousLocPossible={!!draft.sousLocTarget}
            note={note} />
          {dirty && (
            <div className="act" style={{ marginTop: '10px' }}>
              <button className="bt bt-p" data-testid="parc-enregistrer"
                onClick={draft.handleSave}>● Enregistrer les modifications</button>
            </div>
          )}
          {feuilleProprio && (
            <FeuilleNouveauProprio brouillonEnAttente={dirty}
              onFermer={() => setFeuilleProprio(false)}
              onCreer={async (saisie) => {
                const r = await draft.creerProprio(saisie)
                if (r.ok) {
                  setFeuilleProprio(false)
                  setNouveauPropId(r.id)
                  if (r.avertissement) alert(r.avertissement)
                }
                return r
              }} />
          )}
          {/* Un logement se crée CHEZ son propriétaire (mécanique du panel
              conservée : « + Logement » vivait sous chaque proprio) — la
              feuille demande lequel, puis ouvre la fiche du nouveau. */}
          {feuilleNouveau && (
            <Feuille titre="Nouveau logement" testId="feuille-nouveau-logement"
              onFermer={() => setFeuilleNouveau(false)}
              enfants={(
                <>
                  <p style={{ fontSize: '12px', color: 'var(--gris)',
                    margin: '0 0 10px', lineHeight: '1.5' }}>
                    Chez quel propriétaire ?</p>
                  {(c.props || []).filter((p) => p.id !== 'p6' && !estProprioVide(p)).map((p) => {
                    const propIdx = (c.props || []).indexOf(p)
                    return (
                      <button key={p.id} className="bt bt-s"
                        data-testid={`nouveau-log-prop-${p.id}`}
                        style={{ width: '100%', marginBottom: '7px', textAlign: 'left' }}
                        onClick={() => {
                          draft.addLog(propIdx)
                          setFeuilleNouveau(false)
                          setOnglet('info')
                        }}>{propDisplayName(p) || p.nom || p.id}</button>
                    )
                  })}
                  <div className="act">
                    <button className="bt bt-s"
                      onClick={() => setFeuilleNouveau(false)}>Annuler</button>
                  </div>
                </>
              )} />
          )}
        </div>
      </>
    )
  }

  // ── LA FICHE (dix sous-onglets) ───────────────────────────────────
  const entity = selected.entity
  const proprioNom = propDisplayName(selected.prop) || selected.prop.nom || ''
  const sousTitre = selected.isChambre
    ? `${proprioNom} · chambre de la maison`
    : `${proprioNom} · logement`

  return (
    <>
      <BandeauFiche
        sousTitre={sousTitre}
        titre={entity.nom_complet || entity.nom || entity.id}
        dirty={dirty} savedAt={savedAt}
        onSave={draft.handleSave}
        onRetour={() => setSelectedId(null)} />
      <BasculeFiche actif={onglet} onSous={setOnglet}
        visibles={['lc1', 'lc2', 'lc3'].includes(entity.id)
          ? undefined
          : FICHE_ONGLETS.map((o) => o.id).filter((id) => id !== 'savoir')} />
      <div className="page">
        {/* ⚠️ MONTAGE EN COURS (lot 4) — chaque sous-onglet se branche ici au
            fil du lot ; AUCUN placeholder ne doit survivre à la démo. */}
        <FicheOngletMonte
          onglet={onglet}
          setOnglet={setOnglet}
          draft={draft}
          data={data}
          invoicesList={invoicesList}
          invoicesRaw={invoicesRaw}
          incidents={incidents} />
      </div>
    </>
  )
}

/* Routeur des sous-onglets — rempli au fil du montage (lot 4). */
function FicheOngletMonte({ onglet, setOnglet, draft, data, invoicesList, invoicesRaw, incidents }) {
  if (onglet === 'info') {
    return <FicheInfosCtrl draft={draft} data={data}
      invoicesList={invoicesList} invoicesRaw={invoicesRaw} incidents={incidents} />
  }
  if (onglet === 'instr') {
    return <FicheInstructionsCtrl draft={draft}
      onAllerLivret={() => setOnglet('livret')} />
  }
  if (onglet === 'livret') {
    return <FicheLivretCtrl draft={draft}
      onAllerInstructions={() => setOnglet('instr')} />
  }
  if (onglet === 'check') return <FicheChecklistCtrl draft={draft} />
  if (onglet === 'linge') return <FicheLingeCtrl />
  if (onglet === 'docs') return <FicheDocsCtrl draft={draft} />
  if (onglet === 'site') return <FicheSiteCtrl draft={draft} />
  if (onglet === 'services') return <FicheServicesCtrl draft={draft} />
  if (onglet === 'avis') return <FicheAvisCtrl draft={draft} />
  if (onglet === 'savoir') return <FicheSavoirCtrl draft={draft} />
  return (
    <div className="carte" data-testid={`fiche-panneau-${onglet}`}>
      <p style={{ fontSize: '12px', color: 'var(--gris)', margin: 0 }}>
        montage en cours — {onglet}
      </p>
    </div>
  )
}
